/**
 * Compact "Backups" widget shown in the header of every admin page.
 *
 * Responsibilities (Layer 3 + 4):
 *   - Show a health dot (green / yellow / red) based on the roll-up color
 *     of the local + offsite backup states.
 *   - Show the last-backup timestamp in relative form ("12 min ago") and
 *     size in MB.
 *   - Expose two one-click actions:
 *       "Save Now"        → POST /api/admin/backup/snapshot-now
 *       "Download Latest" → GET  /api/admin/backup/download-latest
 *   - Deep-link to the detailed page at /admin/backups.
 *
 * The user just recovered from a database wipe — this widget must render
 * instantly on every admin page so a snapshot is always one click away.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { API_ROOT } from "../lib/api";
import { formatRelative, formatBytes } from "./backupUtils";

const REFRESH_MS = 60_000; // one minute is plenty; snapshots are 30-min cadence

export function BackupWidget() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null); // { kind: "ok"|"warn"|"err", text }

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/backup/health");
      setHealth(data);
    } catch {
      setHealth({ color: "red", overallColor: "red", lastBackupAt: null,
                  backupCount: 0, error: "health check failed" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Auto-dismiss the transient note after 5s.
  useEffect(() => {
    if (!note) return undefined;
    const id = setTimeout(() => setNote(null), 5000);
    return () => clearTimeout(id);
  }, [note]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setNote(null);
    try {
      const { data } = await api.post("/admin/backup/snapshot-now");
      setNote({
        kind: "ok",
        text: `Saved: ${data.name} (${formatBytes(data.sizeBytes || 0)})`,
      });
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429 && detail?.retryAfterMinutes) {
        // Rate-limit hit — friendly amber note, button stays clickable.
        setNote({
          kind: "warn",
          text: `Rate limit reached — 6 saves per hour. Next save in ${detail.retryAfterMinutes} min.`,
        });
      } else {
        setNote({
          kind: "err",
          text: typeof detail === "string"
            ? detail : (detail?.message || e?.message || "Snapshot failed"),
        });
      }
    } finally {
      setSaving(false);
    }
  }, [refresh]);

  const onDownloadLatest = useCallback(() => {
    // Use full URL (not axios) so the browser triggers the file download
    // dialog directly instead of buffering the tar.gz in JS memory.
    window.location.href = `${API_ROOT}/admin/backup/download-latest`;
  }, []);

  const color = health?.overallColor || health?.color || (loading ? "gray" : "red");
  const dotColor = {
    green: "#2f9d5c", yellow: "#e0a415", red: "#c8402b", gray: "#9aa1a8",
  }[color] || "#9aa1a8";
  const lastAt = health?.lastBackupAt || null;
  const canDownload = (health?.backupCount || 0) > 0;

  return (
    <div className="backup-widget" data-testid="backup-widget">
      <div className="backup-widget__row">
        <span
          className="backup-widget__dot"
          style={{ backgroundColor: dotColor }}
          aria-label={`backup health ${color}`}
          data-testid="backup-widget-dot"
          data-color={color}
        />
        <div className="backup-widget__meta">
          <div className="backup-widget__title">Backups</div>
          <div
            className="backup-widget__sub"
            data-testid="backup-widget-last-backup"
          >
            {loading
              ? "Checking…"
              : lastAt
                ? `Last: ${formatRelative(lastAt)}`
                : "No backups yet"}
            {health?.backupDirBytes != null && (
              <> · <span data-testid="backup-widget-size">
                {formatBytes(health.backupDirBytes)}
              </span></>
            )}
          </div>
        </div>
        <div className="backup-widget__actions">
          <button
            type="button"
            className="backup-widget__btn backup-widget__btn--primary"
            data-testid="backup-widget-save-now"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Now"}
          </button>
          <button
            type="button"
            className="backup-widget__btn"
            data-testid="backup-widget-download-latest"
            onClick={onDownloadLatest}
            disabled={!canDownload}
            title={canDownload ? "Download the most recent dump as tar.gz"
                               : "No backups available"}
          >
            Download Latest
          </button>
        </div>
      </div>
      <div className="backup-widget__row backup-widget__foot">
        <Link
          to="/admin/backups"
          className="backup-widget__link"
          data-testid="backup-widget-view-all"
        >
          View all backups →
        </Link>
        {note && (
          <span
            className={`backup-widget__note backup-widget__note--${note.kind}`}
            data-testid={`backup-widget-note-${note.kind}`}
          >
            {note.text}
          </span>
        )}
      </div>
    </div>
  );
}
